/**
 * @fileoverview Date Change Quota Modal component.
 * @summary Modal for blocking date change when quota is exceeded, with CTA to upgrade to ONE_SHOT.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/DateChangeQuotaModal
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faBolt, faExclamationTriangle, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { SUBSCRIPTION_PLANS, SubscriptionType } from '@karaonow/shared'
import Spinner from '../../../components/Spinner'
import type { FC } from 'react'

interface DateChangeQuotaModalProps {
  eventName: string
  isOpen: boolean
  isLoading?: boolean
  quotaMessage: string
  currentCount?: number
  limit?: number
  onUpgradeToOneShot: () => void
  onCancel: () => void
}

/**
 * @summary Modal for blocking date change when quota is exceeded.
 * @param {DateChangeQuotaModalProps} props - Component props
 * @returns {ReactElement | null} Quota exceeded modal or null if not open
 */
const DateChangeQuotaModal: FC<DateChangeQuotaModalProps> = ({
  eventName,
  isOpen,
  isLoading = false,
  quotaMessage,
  currentCount,
  limit,
  onUpgradeToOneShot,
  onCancel,
}) => {
  const { t } = useTranslation()

  if (!isOpen) return null

  const oneShotPlan = SUBSCRIPTION_PLANS[SubscriptionType.ONE_SHOT]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-2 pt-16 bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full flex flex-col max-h-[90vh]">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-4 sm:p-6 shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-100 dark:bg-red-900/30 shrink-0">
              <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              {t('events.quotaExceededDateChangeTitle') || 'Quota mensuel atteint'}
            </h2>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0"
            aria-label={t('common.close')}
          >
            <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
          {/* Error Message */}
          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-900 dark:text-red-200">
              {quotaMessage}
            </p>
            {currentCount !== undefined && limit !== undefined && (
              <p className="text-xs text-red-700 dark:text-red-300 mt-2">
                {t('events.quotaExceededDetails', { current: currentCount, limit }) || 
                  `Vous avez ${currentCount} événement(s) sur ${limit} pour cette période.`}
              </p>
            )}
          </div>

          {/* Explanation */}
          <div>
            <p className="text-gray-700 dark:text-gray-300 mb-2">
              {t('events.quotaExceededDateChangeExplanation', { eventName }) || 
                `Vous ne pouvez pas changer la date de l'événement "${eventName}" car cela dépasserait votre quota mensuel d'événements.`}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('events.quotaExceededDateChangeOptions') || 
                'Vous pouvez soit garder la date originale, soit convertir cet événement en "Événement Unique" pour le sortir de votre quota mensuel.'}
            </p>
          </div>

          {/* ONE_SHOT Option */}
          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <div className="flex items-start gap-2 mb-3">
              <FontAwesomeIcon icon={faBolt} className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200 mb-1">
                  {t('events.upgradeToOneShotOption') || 'Option : Passer en Événement Unique'}
                </h3>
                <p className="text-xs text-indigo-700 dark:text-indigo-300 mb-2">
                  {t('events.upgradeToOneShotDescription') || 
                    'En convertissant cet événement en "Événement Unique", il ne comptera plus dans votre quota mensuel et vous pourrez changer sa date librement.'}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('subscription.price') || 'Prix'}
                  </span>
                  <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                    €{oneShotPlan.priceEur?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Premium Suggestion */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <FontAwesomeIcon icon={faInfoCircle} className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-blue-900 dark:text-blue-200 mb-2">
                  {t('events.multipleEventsPremiumSuggestion') || 
                    'Plusieurs événements ? Le plan Premium pourrait être plus économique.'}
                </p>
                <Link
                  to="/subscription"
                  className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
                >
                  {t('subscription.comparePlans') || 'Comparer les plans →'}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Sticky */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 shrink-0 bg-white dark:bg-gray-800">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="btn btn-secondary order-2 sm:order-1"
          >
            {t('events.keepOriginalDate') || 'Garder la date originale'}
          </button>
          <button
            type="button"
            onClick={onUpgradeToOneShot}
            disabled={isLoading}
            className="btn btn-primary flex items-center justify-center gap-2 order-1 sm:order-2"
          >
            {isLoading ? (
              <>
                <Spinner size="sm" variant="white" />
                <span>{t('common.processing') || 'Traitement'}...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faBolt} className="w-4 h-4" />
                <span>{t('events.upgradeToOneShotNow') || '🚀 Passer en Événement Unique'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DateChangeQuotaModal
